import { SelectProps } from "@cloudscape-design/components";

export type SugarScale = 'sg' | 'brix' | 'balling' | 'plato';
export type AbvMethod = 'simple' | 'berry' | 'duncan-acton' | 'cutaia' | 'reid-speers';

// Options for UI selects
export const sugarScaleOptions: SelectProps.Option[] = [
  { label: 'Specific Gravity (SG)', value: 'sg' },
  { label: 'Brix (°Bx)', value: 'brix' },
  { label: 'Balling (°B)', value: 'balling' },
  { label: 'Plato (°P)', value: 'plato' },
];

export const abvMethodOptions: SelectProps.Option[] = [
  { label: 'Simple / Standard', value: 'simple' },
  { label: 'Berry', value: 'berry' },
  { label: 'Duncan & Acton', value: 'duncan-acton' },
  { label: 'Cutaia (2009)', value: 'cutaia' },
  { label: 'Reid & Speers', value: 'reid-speers' },
];

// Convert Brix/Balling/Plato to specific gravity. These scales are nearly
// identical so we use the same conversion formula for all of them.
export const brixToSG = (brix: number): number => {
  return 1 + brix / (258.6 - ((brix / 258.2) * 227.1));
};

// Reverse conversion from SG to Brix/Plato/Balling
export const sgToBrix = (sg: number): number => {
  return (
    (182.4601 * sg - 775.6821) * sg + 1262.7794
  ) * sg - 669.5622;
};

// Convert any supported sugar scale to specific gravity
export const toSpecificGravity = (value: number, scale: SugarScale): number => {
  switch (scale) {
    case 'brix':
    case 'balling':
    case 'plato':
      return brixToSG(value);
    case 'sg':
    default:
      return value;
  }
};

// Convert from specific gravity to the selected sugar scale
export const fromSpecificGravity = (sg: number, scale: SugarScale): number => {
  switch (scale) {
    case 'brix':
    case 'balling':
    case 'plato':
      return sgToBrix(sg);
    case 'sg':
    default:
      return sg;
  }
};

// Helper: SG to Plato using polynomial approximation
const sgToPlato = (sg: number): number => {
  return -616.868 + 1111.14 * sg - 630.272 * sg * sg + 135.997 * sg * sg * sg;
};

// ABV estimation methods
export const calculateAbv = (og: number, fg: number, method: AbvMethod): number => {
  switch (method) {
    case 'berry':
      return (1.05 * (og - fg) / fg) / 0.79 * 100;
    case 'duncan-acton': {
      const abwDA = (og - fg) / (2.0665 - 0.010665 * og);
      return abwDA * (fg / 0.794) * 100;
    }
    case 'cutaia':
      return (76.08 * (og - fg) / (1.775 - og)) * (fg / 0.794);
    case 'reid-speers': {
      const ogP = sgToPlato(og);
      const fgP = sgToPlato(fg);
      const abwRS = (ogP - fgP) / (2.0665 - 0.010665 * ogP);
      return abwRS * (fg / 0.794);
    }
    case 'simple':
    default:
      return (og - fg) * 131.25;
  }
};
